/* =====================================================================
 * DocumentEditor — styled-components
 * ===================================================================== */

import styled from 'styled-components';

/* ── 레이아웃 ── */

export const EditorWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

export const EditorBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
  padding-left: max(72px, calc((100% - 720px) / 2));
  padding-right: max(16px, calc((100% - 720px) / 2));
  padding-top: 40px;
  padding-bottom: 240px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-top: 20px;
    padding-right: 16px;
    padding-bottom: 160px;
  }
`;

export const TitleArea = styled.textarea`
  display: block;
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  overflow: hidden;
  background: transparent;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 16px;

  &::placeholder {
    color: #9ca3af;
    opacity: 1;
  }
`;

/* ── DragHandle 내부 레이아웃 ── */

export const HandleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  position: relative;
`;

/* 테이블/컨테이너 전용 커스텀 핸들 */
export const TableHandleRow = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  position: absolute;
  z-index: 50;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.1s ease;
`;

export const HandleBtn = styled.div`
  width: 20px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.45;
  background: rgba(0, 0, 0, 0.04);
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: opacity 0.15s, background 0.15s;

  &:hover {
    opacity: 0.85;
    background: rgba(0, 0, 0, 0.08);
  }
`;

/* ── + 드롭다운 메뉴 ── */

export const SlashDropdown = styled.div<{ $direction: 'down' | 'up' }>`
  position: absolute;
  width: 280px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#2a2a2a' : '#ffffff'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 999;
  display: flex;
  flex-direction: ${({ $direction }) =>
    $direction === 'down' ? 'column' : 'column-reverse'};
  overflow: hidden;
`;

export const DropdownScroll = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
`;

export const DropdownSection = styled.div`
  padding: 8px 14px 4px;
  font-size: 0.72rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: none;
  letter-spacing: 0;
`;

export const DropdownItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: background 0.1s;
  background: ${({ theme, $active }) =>
    $active
      ? theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
      : 'transparent'};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  }
`;

export const DropdownIcon = styled.div`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const DropdownLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

export const DropdownShortcut = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.6;
  flex-shrink: 0;
`;

export const DropdownFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-top: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  }
`;

/* ================================================================== */
/* EditorArea                                                          */
/* ================================================================== */

export const EditorArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 420px;
  overflow: visible;

  /*
   * DragHandle element 자체가 넓게 클릭을 먹지 않도록 차단하고,
   * 실제 버튼 자식만 클릭 가능하게 한다.
   */
  .drag-handle {
    pointer-events: none !important;
  }
  .drag-handle > * {
    pointer-events: auto !important;
  }

  .doc-editor-content {
    outline: none;
    height: 100%;
    min-height: 100%;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 1rem;
    line-height: 1.85;
    color: ${({ theme }) => theme.colors.textPrimary};
    /* 왼쪽 패딩 — 드래그 핸들 영역을 에디터 DOM에 포함시켜
       핸들로 마우스 이동해도 mouseleave 발생 안 되게 함 */
    padding-left: 72px;
    margin-left: -72px;

    > * + * {
      margin-top: 8px;
    }

    /* 텍스트/헤딩은 좌측 정렬감 유지 (Notion 스타일) */
    > p, > h1, > h2, > h3 {
      padding: 6px 0;
      border-radius: 6px;
    }

    /* 나머지 블록은 기존 여백 유지 */
    > blockquote, > pre, > ul, > ol,
    > div[data-type="details"],
    > details.doc-details {
      padding: 6px 10px;
      border-radius: 6px;
    }

    /* ★ tableWrapper는 padding 없이 margin 만 사용
       (padding은 ProseMirror의 posAtCoords 좌표를 밀어냄) */
    > .tableWrapper {
      margin: 6px 10px;
      border-radius: 6px;
    }

    /* ── Headings ── */
    h1 { font-size: 1.875rem; font-weight: 700; line-height: 1.3; margin-top: 1.5em; }
    h2 { font-size: 1.5rem; font-weight: 600; line-height: 1.35; margin-top: 1.25em; }
    h3 { font-size: 1.25rem; font-weight: 600; line-height: 1.4; margin-top: 1em; }

    /* ── Paragraph ── */
    p { margin: 0; }

    /* ── Placeholder ── */
    /* placeholder는 top-level 텍스트 블록에만 표시
       (table row/cell에 적용되면 유령 셀처럼 렌더되어 열 오프셋 유발) */
    > p.is-empty[data-placeholder]::before,
    > p.is-editor-empty[data-placeholder]::before,
    > h1.is-empty[data-placeholder]::before,
    > h1.is-editor-empty[data-placeholder]::before,
    > h2.is-empty[data-placeholder]::before,
    > h2.is-editor-empty[data-placeholder]::before,
    > h3.is-empty[data-placeholder]::before,
    > h3.is-editor-empty[data-placeholder]::before {
      content: attr(data-placeholder);
      float: left;
      color: ${({ theme }) => theme.colors.textMuted};
      opacity: 0.5;
      pointer-events: none;
      height: 0;
    }

    /* 표 내부는 절대 placeholder 표시하지 않음 (보험) */
    td [data-placeholder]::before,
    th [data-placeholder]::before,
    td .is-empty::before,
    th .is-empty::before {
      content: none !important;
      display: none !important;
    }

    /* ── Lists (노션/토글 스타일 커스텀) ── */
    ul:not([data-type="taskList"]),
    ol {
      list-style: none;
      padding-left: 0;
      margin: 0;

      > li {
        display: grid;
        grid-template-columns: 22px minmax(0, 1fr);
        column-gap: 4px;
        align-items: start;
        margin: 0;

        > * {
          min-width: 0;
          word-break: break-word;
        }

        > :not(ul):not(ol) {
          grid-column: 2;
          position: relative;
          top: 6px;
        }

        > ul:not([data-type="taskList"]),
        > ol {
          grid-column: 2;
          margin: 0;
        }

        > p,
        > div {
          margin: 0;
        }

        &::before {
          grid-column: 1;
          align-self: start;
          width: 22px;
          text-align: center;
          font-size: 1em;
          line-height: 1.85;
          position: relative;
          top: 6px;
          color: ${({ theme }) => theme.colors.textSecondary};
        }
      }

      /* 중첩 리스트 들여쓰기 */
      ul:not([data-type="taskList"]),
      ol {
        padding-left: 0;
      }
    }

    /* ── 글머리 기호 (bullet) ── */
    ul:not([data-type="taskList"]) > li::before {
      content: '•';
      font-size: 1.5em;
      line-height: calc(1.85 / 1.5);
    }
    ul:not([data-type="taskList"]) ul:not([data-type="taskList"]) > li::before {
      content: '◦';
      font-size: 1.4em;
      line-height: calc(1.85 / 1.4);
    }
    ul:not([data-type="taskList"]) ul:not([data-type="taskList"]) ul:not([data-type="taskList"]) > li::before {
      content: '▪';
      font-size: 0.9em;
      line-height: calc(1.85 / 0.9);
    }

    /* ── 번호 매기기 ── */
    ol {
      counter-reset: list-counter;
    }
    ol > li {
      counter-increment: list-counter;
    }
    ol > li::before {
      content: counter(list-counter, decimal) '.';
      text-align: right;
      color: ${({ theme }) => theme.colors.textPrimary};
    }
    ol ol > li::before {
      content: counter(list-counter, lower-alpha) '.';
    }
    ol ol ol > li::before {
      content: counter(list-counter, lower-roman) '.';
    }
    ol ol ol ol > li::before {
      content: counter(list-counter, decimal) '.';
    }

    /* ── Task List ── */
    ul[data-type="taskList"] {
      list-style: none;
      padding-left: 0;

      li {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: 2px 0;

        > label {
          display: flex;
          align-items: center;
          margin-top: 5px;
          flex-shrink: 0;
          cursor: pointer;

          input[type="checkbox"] {
            appearance: none;
            -webkit-appearance: none;
            width: 16px;
            height: 16px;
            border: 2px solid ${({ theme }) =>
              theme.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.25)'};
            border-radius: 3px;
            cursor: pointer;
            position: relative;
            transform: translateY(2px);
            transition: all 0.15s ease;
            background: transparent;

            &:checked {
              background: ${({ theme }) => theme.colors.info || '#3b82f6'};
              border-color: ${({ theme }) => theme.colors.info || '#3b82f6'};

              &::after {
                content: '';
                position: absolute;
                left: 3px;
                top: 0px;
                width: 6px;
                height: 9px;
                border: solid #fff;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
              }
            }

            &:hover {
              border-color: ${({ theme }) => theme.colors.info || '#3b82f6'};
            }
          }
        }

        > div {
          flex: 1;
          min-width: 0;
        }

        &[data-checked="true"] > div {
          text-decoration: line-through;
          opacity: 0.5;
        }
      }

      /* 중첩 할일 목록 들여쓰기 */
      li > div > ul[data-type="taskList"] {
        padding-left: 26px;
        margin-top: 2px;
      }
    }

    /* ── Blockquote ── */
    blockquote {
      border-left: 3px solid ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)'};
      margin: 0.75em 0;
      padding: 2px 0 2px 16px;
      color: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.6)'};
    }

    /* ── Toggle (Details / Summary) — 노션 스타일 ──
     * tiptap Details v3 NodeView DOM 구조:
     *   div[data-type="details"]
     *     button                         ← 토글 버튼
     *     div (contentDOM)
     *       div[data-type="detailsSummary"] > p
     *       div[data-type="detailsContent"] > p
     * 열린 상태: .is-open 클래스 */
    div[data-type="details"] {
      border: none;
      border-radius: 0;
      margin: 0;
      background: transparent;
      display: flex;
      align-items: flex-start;

      /* ── 토글 버튼 (▶ 아이콘) ── */
      > button {
        all: unset;
        flex-shrink: 0;
        width: 22px;
        height: 1.85em;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        cursor: pointer;
        background-repeat: no-repeat;
        background-position: center;
        background-size: 18px 18px;
        margin-right: 8px;
        background-image: ${({ theme }) =>
          theme.mode === 'dark'
            ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10'%3E%3Cpath d='M3 1.5L8 5L3 8.5Z' fill='%23aeaeae'/%3E%3C/svg%3E")`
            : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10'%3E%3Cpath d='M3 1.5L8 5L3 8.5Z' fill='%23787774'/%3E%3C/svg%3E")`};
        transition: transform 0.15s ease, background-color 0.1s;
      }
      > button:hover {
        background-color: ${({ theme }) =>
          theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
      }

      /* 열린 상태: 화살표 90도 회전 */
      &.is-open > button {
        transform: rotate(90deg);
      }

      /* ── contentDOM (summary + content 래퍼) ── */
      > div {
        flex: 1;
        min-width: 0;
      }

      /* ── Summary ── */
      summary {
        padding: 0;
      }

      /* ── Summary 플레이스홀더 ("토글") ── */
      summary.is-empty[data-placeholder]::before {
        content: attr(data-placeholder);
        float: left;
        height: 0;
        color: ${({ theme }) => theme.colors.textMuted};
        opacity: 0.5;
        pointer-events: none;
      }

      /* ── Content 영역 ── */
      div[data-type="detailsContent"] {
        padding: 0 0 4px 0;
      }

      /* 닫힌 상태: 내용 숨김 */
      &:not(.is-open) div[data-type="detailsContent"] {
        display: none;
      }

      /* ── 빈 content 플레이스홀더 ── */
      div[data-type="detailsContent"] > p.is-empty[data-placeholder]::before {
        content: attr(data-placeholder);
        color: ${({ theme }) => theme.colors.textMuted};
        opacity: 0.5;
        pointer-events: none;
        float: left;
        height: 0;
      }
    }

    /* ── Code Block ── */
    pre.doc-code-block {
      position: relative;
      margin: 10px 0;
      padding: 28px 16px 14px;
      border-radius: 10px;
      border: 1px solid ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.025)'};
      box-shadow: ${({ theme }) =>
        theme.mode === 'dark'
          ? 'inset 0 1px 0 rgba(255,255,255,0.03)'
          : 'inset 0 1px 0 rgba(255,255,255,0.7)'};
      overflow-x: auto;
      overflow-y: hidden;
      font-family: ${({ theme }) => theme.fonts.mono};
      font-size: 0.86rem;
      line-height: 1.65;
      -webkit-font-smoothing: antialiased;
      scrollbar-width: thin;

      code {
        background: none;
        padding: 0;
        color: inherit;
        font-size: inherit;
        border-radius: 0;
      }

      /* 언어 라벨 (::before) */
      &::before {
        content: attr(data-language) ' ▾';
        position: absolute;
        top: 6px;
        right: 38px;
        z-index: 2;
        display: inline-flex;
        align-items: center;
        height: 20px;
        padding: 0 8px;
        font-family: ${({ theme }) => theme.fonts.sans};
        font-size: 0.7rem;
        font-weight: 500;
        color: ${({ theme }) =>
          theme.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.35)'};
        border-radius: 4px;
        pointer-events: none;
        transition: color 0.15s;
      }

      /* 복사 버튼 (::after) */
      &::after {
        content: '';
        position: absolute;
        top: 6px;
        right: 10px;
        width: 22px;
        height: 20px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='9' y='9' width='13' height='13' rx='2'/%3E%3Cpath d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 14px;
        cursor: pointer;
        border-radius: 4px;
        opacity: 0.5;
        transition: opacity 0.15s;
        pointer-events: none;
      }

      &:hover::before { color: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)'}; }
      &:hover::after { opacity: 0.85; }

      /* 복사 완료 상태 */
      &.doc-code-copied::after {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2322c55e' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E");
        opacity: 1;
      }
    }

    /* ── Syntax Highlighting (lowlight / hljs) ── */
    .hljs-comment,
    .hljs-quote {
      color: ${({ theme }) => theme.mode === 'dark' ? '#6a737d' : '#6a737d'};
      font-style: italic;
    }
    .hljs-keyword,
    .hljs-selector-tag {
      color: ${({ theme }) => theme.mode === 'dark' ? '#ff7b72' : '#d73a49'};
    }
    .hljs-literal,
    .hljs-number,
    .hljs-tag .hljs-attr {
      color: ${({ theme }) => theme.mode === 'dark' ? '#79c0ff' : '#005cc5'};
    }
    .hljs-string,
    .hljs-doctag,
    .hljs-regexp {
      color: ${({ theme }) => theme.mode === 'dark' ? '#a5d6ff' : '#032f62'};
    }
    .hljs-title,
    .hljs-name,
    .hljs-section,
    .hljs-selector-id {
      color: ${({ theme }) => theme.mode === 'dark' ? '#d2a8ff' : '#6f42c1'};
      font-weight: bold;
    }
    .hljs-subst { color: ${({ theme }) => theme.mode === 'dark' ? '#c9d1d9' : '#24292e'}; }
    .hljs-type,
    .hljs-class .hljs-title {
      color: ${({ theme }) => theme.mode === 'dark' ? '#ffa657' : '#e36209'};
    }
    .hljs-built_in,
    .hljs-builtin-name {
      color: ${({ theme }) => theme.mode === 'dark' ? '#ffa657' : '#e36209'};
    }
    .hljs-variable,
    .hljs-template-variable {
      color: ${({ theme }) => theme.mode === 'dark' ? '#ffa657' : '#e36209'};
    }
    .hljs-attr { color: ${({ theme }) => theme.mode === 'dark' ? '#79c0ff' : '#005cc5'}; }
    .hljs-params { color: ${({ theme }) => theme.mode === 'dark' ? '#c9d1d9' : '#24292e'}; }
    .hljs-meta { color: ${({ theme }) => theme.mode === 'dark' ? '#79c0ff' : '#005cc5'}; }
    .hljs-deletion { color: #ffa8a8; background: rgba(255,0,0,0.1); }
    .hljs-addition { color: #7ee787; background: rgba(0,255,0,0.1); }
    .hljs-emphasis { font-style: italic; }
    .hljs-strong { font-weight: bold; }

    /* ── Inline Code ── */
    code {
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
      border-radius: 4px;
      padding: 2px 6px;
      font-family: ${({ theme }) => theme.fonts.mono};
      font-size: 0.875em;
    }

    /* ── Horizontal Rule ── */
    hr {
      border: none;
      border-top: 1px solid ${({ theme }) => theme.colors.border};
      margin: 1.5em 0;
    }

    /* ── Link ── */
    a {
      color: ${({ theme }) => theme.colors.info};
      text-decoration: underline;
      cursor: pointer;
    }

    /* ── Mark / Highlight ── */
    mark {
      background-color: #fef08a;
      border-radius: 2px;
      padding: 1px 2px;
    }

    /* ── Image (legacy <img> from old data) ── */
    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 8px 0;
      cursor: pointer;

      &.ProseMirror-selectednode {
        outline: 2px solid #f59e0b;
        border-radius: 8px;
      }
    }

    /* ── Media Block NodeViews (image/video/audio/file/bookmark) ── */
    div[data-type="image-block"],
    div[data-type="video-block"],
    div[data-type="audio-block"],
    div[data-type="file-block"],
    div[data-type="bookmark-block"] {
      margin: 4px 0;

      &.ProseMirror-selectednode {
        > .react-renderer > [data-node-view-wrapper] > div {
          outline: 2px solid ${({ theme }) =>
            theme.mode === 'dark' ? 'rgba(251,191,36,0.55)' : 'rgba(245,158,11,0.45)'};
          border-radius: 8px;
        }
      }
    }

    /* ── Table ── */
    table {
      border-collapse: collapse;
      table-layout: fixed;
      width: 100%;
      min-width: 100%;
      overflow: visible;
      margin: 16px 0;
      border-radius: 8px;
      border: 1px solid ${({ theme }) => theme.colors.border};

      th, td {
        border: 1px solid ${({ theme }) => theme.colors.border};
        padding: 8px 12px;
        text-align: left;
        vertical-align: top;
        min-width: 80px;
        box-sizing: border-box;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        position: relative;
      }

      th p,
      td p {
        margin: 0;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: anywhere;
      }

      th {
        background: ${({ theme }) =>
          theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'};
        font-weight: 600;
      }

      .selectedCell::after {
        content: '';
        position: absolute;
        inset: 0;
        background: ${({ theme }) =>
          theme.mode === 'dark' ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.1)'};
        pointer-events: none;
      }
    }

    .tableWrapper {
      overflow: visible;
      position: relative;
      padding-bottom: 36px;
    }

    /* 실제 가로 스크롤은 테이블 블록 내부에서만 발생 */
    .tableWrapper > .table-scroll {
      position: relative;
      max-width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
    }

    /* ── 열 리사이즈 핸들 (셀 오른쪽 경계 드래그) ── */
    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: 0;
      width: 4px !important;
      z-index: 20;
      pointer-events: auto;
      background: ${({ theme }) => theme.colors.info || '#3b82f6'} !important;
      cursor: col-resize;
      opacity: 0;
      transition: opacity 0.15s;
    }
    table:hover .column-resize-handle {
      opacity: 0.35;
    }
    .column-resize-handle:hover,
    .column-resize-handle:active {
      opacity: 1 !important;
    }
    &.resize-cursor {
      cursor: col-resize !important;
    }

    /* ── 열 선택 핸들 (1행 각 셀 위쪽 테두리 가운데, 고정 크기) ── */
    .table-col-handle {
      position: absolute;
      width: 24px;
      height: 5px;
      border-radius: 3px;
      cursor: pointer;
      z-index: 25;
      display: none;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => theme.colors.borderInput};
      transition: height 0.1s ease;
      pointer-events: none;
      overflow: hidden;
      transform: translateY(-50%);
    }
    .table-col-handle .handle-grip {
      opacity: 0;
      transition: opacity 0.1s;
      color: ${({ theme }) => theme.colors.bgSecondary};
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .table-col-handle:hover {
      height: 14px;
    }
    .table-col-handle:hover .handle-grip {
      opacity: 1;
    }
    .table-col-handle[data-handle-visible="true"] {
      display: flex;
      pointer-events: auto;
    }

    /* ── 행 선택 핸들 (각 행 1열 왼쪽 테두리 가운데, 고정 크기) ── */
    .table-row-handle {
      position: absolute;
      width: 5px;
      height: 24px;
      border-radius: 3px;
      cursor: pointer;
      z-index: 25;
      display: none;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => theme.colors.borderInput};
      transition: width 0.1s ease;
      pointer-events: none;
      overflow: hidden;
      transform: translateX(-50%);
    }
    .table-row-handle .handle-grip {
      opacity: 0;
      transition: opacity 0.1s;
      color: ${({ theme }) => theme.colors.bgSecondary};
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .table-row-handle:hover {
      width: 14px;
    }
    .table-row-handle:hover .handle-grip {
      opacity: 1;
    }
    .table-row-handle[data-handle-visible="true"] {
      display: flex;
      pointer-events: auto;
    }

    /* ── 테이블 행 추가 버튼 ── */
    .table-add-row-btn {
      position: absolute;
      left: 0;
      top: 0;
      height: 20px;
      display: none;
      align-items: center;
      justify-content: center;
      border: 1.5px dashed transparent;
      border-radius: 4px;
      background: transparent;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s, background 0.15s, border-color 0.15s;
      z-index: 5;
      color: ${({ theme }) => theme.colors.textSecondary};
      padding: 0;
      pointer-events: none; /* 숨김 상태에서는 클릭 차단 */
    }
    .tableWrapper[data-table-controls-ready="true"][data-table-controls-active="true"] .table-add-row-btn {
      display: flex;
      opacity: 1;
      pointer-events: auto;
      border-color: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
    }
    .table-add-row-btn:hover {
      opacity: 1 !important;
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
      border-color: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'};
      color: ${({ theme }) => theme.colors.textPrimary};
    }

    /* ── 테이블 열 추가 버튼 ── */
    .table-add-col-btn {
      position: absolute;
      width: 20px;
      display: none;
      align-items: center;
      justify-content: center;
      border: 1.5px dashed transparent;
      border-radius: 4px;
      background: transparent;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s, background 0.15s, border-color 0.15s;
      z-index: 5;
      color: ${({ theme }) => theme.colors.textSecondary};
      padding: 0;
      pointer-events: none; /* 숨김 상태에서는 클릭 차단 */
    }
    .tableWrapper[data-table-controls-ready="true"][data-table-controls-active="true"] .table-add-col-btn {
      display: flex;
      opacity: 1;
      pointer-events: auto;
      border-color: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
    }
    .table-add-col-btn:hover {
      opacity: 1 !important;
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
      border-color: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'};
      color: ${({ theme }) => theme.colors.textPrimary};
    }

    /* ── 블록 선택 (grip 클릭 시 주황색 테두리) ── */
    p.ProseMirror-selectednode,
    h1.ProseMirror-selectednode,
    h2.ProseMirror-selectednode,
    h3.ProseMirror-selectednode,
    blockquote.ProseMirror-selectednode,
    ul.ProseMirror-selectednode,
    ol.ProseMirror-selectednode {
      background: transparent;
      padding: 6px 10px;
      border-radius: 6px;
      outline: 2px solid
        ${({ theme }) =>
          theme.mode === 'dark' ? 'rgba(251,191,36,0.35)' : 'rgba(245,158,11,0.3)'};
      outline-offset: -1px;
      box-shadow: inset 0 0 0 1px
        ${({ theme }) =>
          theme.mode === 'dark' ? 'rgba(251,191,36,0.45)' : 'rgba(245,158,11,0.35)'};
    }

    pre.doc-code-block.ProseMirror-selectednode {
      border-color: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(251,191,36,0.45)' : 'rgba(245,158,11,0.38)'};
      box-shadow:
        ${({ theme }) =>
          theme.mode === 'dark'
            ? 'inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 2px rgba(251,191,36,0.35)'
            : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 0 0 2px rgba(245,158,11,0.28)'};
    }

    table.ProseMirror-selectednode {
      background: transparent;
      box-shadow: 0 0 0 2px
        ${({ theme }) =>
          theme.mode === 'dark' ? 'rgba(251,191,36,0.55)' : 'rgba(245,158,11,0.45)'};
      border-radius: 8px;
    }
  }
`;
